import { useState } from 'react';
import {
  ArrowRight,
  X,
  BookOpen,
  Heart,
  TrendingUp,
  Users,
  Shield,
  GraduationCap,
  Globe,
  Database,
  Activity,
} from 'lucide-react';

import '../../index.css';
import './Sectors.css';

const Sectors = () => {
  const [activeSector, setActiveSector] = useState(null);

  const thematicAreas = [
    {
      id: 'education',
      title: 'Education, Skills, and Learning Systems',
      icon: <GraduationCap />,
      intro:
        'We strengthen education systems through evidence-driven reform, assessment, and innovation.',
      cardPoints: [
        'Implementation research on curriculum reform, teacher professional development, and digital learning',
        'MERL systems for education programmes and learning outcomes',
      ],
      fullPoints: [
        'Implementation research on curriculum reform, teacher professional development, and digital learning',
        'MERL systems for education programmes and learning outcomes',
        'Data analytics for assessment systems, learning measurement, and performance monitoring',
        'Policy research and education system diagnostics',
      ],
    },
    {
      id: 'health',
      title: 'Public Health, Nutrition & Health Systems Strengthening',
      icon: <Heart />,
      intro:
        'We support resilient, equitable, and data-driven health and nutrition systems.',
      cardPoints: [
        'Implementation research on service delivery models and community health interventions',
        'Digital MERL systems for health and nutrition programmes',
      ],
      fullPoints: [
        'Implementation research on service delivery models and community health interventions',
        'Impact evaluations and learning reviews',
        'Digital MERL systems for health and nutrition programmes',
        'Predictive analytics for service utilisation, risk, and resource targeting',
      ],
    },
    {
      id: 'governance',
      title: 'Governance, Democracy & Institutional Accountability',
      icon: <Shield />,
      intro:
        'We promote inclusive, transparent, and accountable governance systems.',
      cardPoints: [
        'Political economy analysis and institutional diagnostics',
        'Governance monitoring frameworks and digital scorecards',
      ],
      fullPoints: [
        'Political economy analysis and institutional diagnostics',
        'Implementation research on citizen engagement and service accountability',
        'Governance monitoring frameworks and digital scorecards',
        'Data systems strengthening for public sector performance',
      ],
    },
    {
      id: 'climate',
      title: 'Climate Change, Resilience & Environmental Justice',
      icon: <Globe />,
      intro:
        'We support climate-resilient, community-led, and sustainable development pathways.',
      cardPoints: [
        'Implementation research on climate adaptation and green livelihoods',
        'MERL frameworks for climate resilience and environmental programmes',
      ],
      fullPoints: [
        'Implementation research on climate adaptation and green livelihoods',
        'MERL frameworks for climate resilience and environmental programmes',
        'Data analytics for vulnerability mapping and resilience tracking',
        'Systems strengthening for climate governance and planning',
      ],
    },
    {
      id: 'gender',
      title: 'Gender Equality, Disability & Social Inclusion',
      icon: <Users />,
      intro:
        'We advance equity and social transformation through evidence-based inclusion strategies.',
      cardPoints: [
        'Social norms and gender-transformative implementation research',
        'Equity-focused evaluations and safeguarding systems',
      ],
      fullPoints: [
        'Social norms and gender-transformative implementation research',
        'Equity-focused evaluations and safeguarding systems',
        'Inclusion-sensitive MERL and disaggregated data systems',
        'Institutional systems for safeguarding, inclusion, and accountability',
      ],
    },
    {
      id: 'youth',
      title: 'Youth, Employment & Future Skills',
      icon: <TrendingUp />,
      intro:
        'We equip young people with the skills and resources for productive livelihoods and civic participation.',
      cardPoints: [
        'Implementation research on youth employability and entrepreneurship models',
        'MERL systems for youth and workforce programmes',
      ],
      fullPoints: [
        'Implementation research on youth employability and entrepreneurship models',
        'MERL systems for youth and workforce programmes',
        'Data analytics for labour market forecasting and skills alignment',
        'Systems strengthening for youth services and innovation ecosystems',
      ],
    },
    {
      id: 'social',
      title: 'Social Protection & Economic Inclusion',
      icon: <Activity />,
      intro:
        'We strengthen systems that protect vulnerable populations and expand economic opportunity.',
      cardPoints: [
        'Implementation research on cash transfers, livelihoods, and graduation models',
        'Digital MERL and beneficiary tracking systems',
      ],
      fullPoints: [
        'Implementation research on cash transfers, livelihoods, and graduation models',
        'Digital MERL and beneficiary tracking systems',
        'Analytics for targeting, risk scoring, and programme optimisation',
        'Policy research and systems reform advisory',
      ],
    },
    {
      id: 'data',
      title: 'Data Science, Digital Transformation & ICT4D',
      icon: <Database />,
      intro:
        'We embed digital innovation across development systems.',
      cardPoints: [
        'Advanced analytics and machine learning for development decision-making',
        'Digital system design and data integration',
      ],
      fullPoints: [
        'Advanced analytics and machine learning for development decision-making',
        'Digital system design and data integration',
        'Implementation research on digital platforms and service delivery models',
        'Institutional digital transformation strategies',
      ],
    },
    {
      id: 'community',
      title: 'Community-Led Development & Participatory Systems',
      icon: <BookOpen />,
      intro:
        'We place communities at the centre of sustainable change.',
      cardPoints: [
        'Participatory implementation research and co-creation',
        'Community-based MERL and feedback systems',
      ],
      fullPoints: [
        'Participatory implementation research and co-creation',
        'Community-based MERL and feedback systems',
        'Local data systems and social accountability tools',
        'Knowledge translation for community learning and advocacy',
      ],
    },
  ];

  return (
    <>
      {/* Hero */}
      <section className="hero hero-sectors">
        <div className="container text-center">
          <h1 className="text-primary">Our Thematic Focus Areas</h1>
          <p className="leadall">
            DevLearn delivers its services across interconnected thematic domains, supported by
            applied research & evaluation, MERL systems, data analytics, systems strengthening,
            strategy, governance, and knowledge translation.
          </p>
          <div className="hero-buttons">
            <a href="#domain-specific" className="btn btn-primary">
              Domain-Specific
            </a>
            <a href="#cross-domain" className="btn btn-secondary">
              Cross-Domain
            </a>
          </div>
        </div>
      </section>

      {/* Domain-Specific */}
      <section id="domain-specific" className="section bg-white">
        <div className="container">
          <h2 className="text-primary text-center mb-5">Domain-Specific Insights</h2>

          <div className="sectors-grid-wrapper">
            {thematicAreas.map((area) => (
              <div key={area.id} className="sector-card">
                <div className="sector-icon">{area.icon}</div>
                <h3>{area.title}</h3>

                <p className="sector-intro">{area.intro}</p>

                <ul className="sector-points">
                  {area.cardPoints.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>

                <button
                  className="sector-link"
                  onClick={() => setActiveSector(area)}
                >
                  Learn More <ArrowRight size={18} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-Domain */}
      <section id="cross-domain" className="section bg-light">
        <div className="container">
          <h2 className="text-primary text-center mb-5">Cross-Domain Approach</h2>
          <p className="lead text-center mb-5">
            Our strength lies in connecting multiple thematic areas through integrated,
            systems-thinking approaches. We design and deliver evidence that cuts across
            education, health, climate, gender, governance, and economic inclusion.
          </p>

          <div className="approach-grid">
            <div className="approach-step">
              <h3>1. Systems Mapping & Integration</h3>
              <p>
                Identifying intersections between thematic areas and aligning research,
                implementation, and data systems.
              </p>
            </div>
            <div className="approach-step">
              <h3>2. Multi-Sectoral Evidence Building</h3>
              <p>
                Using mixed-methods research, integrated MERL, and analytics to understand
                cross-cutting impacts and trade-offs.
              </p>
            </div>
            <div className="approach-step">
              <h3>3. Collaborative Implementation & Learning</h3>
              <p>
                Supporting adaptive implementation, stakeholder learning, and institutional
                capacity across sectors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta final-cta-sectors text-center">
        <div className="container">
          <h2>
            Ready to Advance Your Development Goals?
            <br />
            Let’s Design Evidence-Based Solutions Together.
          </h2>
          <a href="/contact" className="btn btn-primary">
            Get in Touch <ArrowRight size={28} />
          </a>
        </div>
      </section>

      {/* Footer */}
      {/* <footer className="footer">
        <div className="footer-grid container">
          <div>
            <div className="footer-logo">DevLearn Impact Research Ltd</div>
            <p>Plot No. 3788/A, Avondale Road, Lusaka</p>
            <p>+260 973 902 981 / +260 978 341 825</p>
            <p>info@devlearnimpact.com</p>
          </div>
        </div>
        <div className="copyright">
          © 2026 DevLearn Impact Research Ltd. All rights reserved.
        </div>
      </footer> */}

      {/* Modal */}
      {activeSector && (
        <div className="modal-overlay" onClick={() => setActiveSector(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setActiveSector(null)}
            >
              <X />
            </button>

            <div className="modal-header">
              <div className="modal-icon">{activeSector.icon}</div>
              <h2>{activeSector.title}</h2>
            </div>

            <p className="modal-intro">{activeSector.intro}</p>

            <ul className="modal-list">
              {activeSector.fullPoints.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

       {/* Footer */}
      <footer className="footer">
        <div className="footer-grid container">
          <div>
            <div className="footer-logo">DevLearn Impact Research Ltd</div>
            <p>Plot No. 3788/A, Avondale Road, Lusaka</p>
            <p>+260 973 902 981 / +260 978 341 825</p>
            <p>info@devlearnimpact.com</p>
          </div>
          <div>
            <h4>Services</h4>
            <ul>
              <li><a href="/services">Applied Research</a></li>
              <li><a href="/services">MERL Frameworks</a></li>
              <li><a href="/services">Capacity Building</a></li>
            </ul>
          </div>
          <div>
            <h4>Thematic Areas</h4>
            <ul>
              <li><a href="/sectors#domain-specific">Domain-Specific</a></li>
              <li><a href="/sectors#cross-domain">Cross-Domain</a></li>
            </ul>
          </div>
          <div>
            <h4>Company</h4>
            <ul>
              <li><a href="/about">About</a></li>
              <li><a href="/team">Team</a></li>
              <li><a href="/case-studies">Projects</a></li>
            </ul>
          </div>
          <div>
            <h4>Connect</h4>
            <ul>
              <li><a href="https://www.linkedin.com/company/devlearn-impact-research/">LinkedIn</a></li>
              <li><a href="https://www.facebook.com/share/1AS3Ahfsy7/">Facebook</a></li>
            </ul>
          </div>
        </div>
        <div className="copyright">
          © 2026 DevLearn Impact Research Ltd. All rights reserved.
        </div>
      </footer>
    </>
  );
};

export default Sectors;

// import { useState } from 'react';
// import {
//   ArrowRight,
//   X,
//   BookOpen,
//   Heart,
//   TrendingUp,
//   Users,
//   Shield,
//   GraduationCap,
//   Globe,
//   Database,
//   Activity,
// } from 'lucide-react';

// import '../../index.css';
// import './Sectors.css';

// const Sectors = () => {
//   const [activeSector, setActiveSector] = useState(null);

//   const thematicAreas = [
//     {
//       id: 'education',
//       title: 'Education, Skills, and Learning Systems',
//       icon: <GraduationCap />,
//       cardPoints: [
//         'Implementation research on curriculum reform, teacher professional development, and digital learning',
//         'MERL systems for education programmes and learning outcomes',
//       ],
//       fullPoints: [
//         'Implementation research on curriculum reform, teacher professional development, and digital learning',
//         'MERL systems for education programmes and learning outcomes',
//         'Data analytics for assessment systems, learning measurement, and performance monitoring',
//         'Policy research and education system diagnostics',
//       ],
//     },
//     {
//       id: 'health',
//       title: 'Public Health, Nutrition & Health Systems Strengthening',
//       icon: <Heart />,
//       cardPoints: [
//         'Implementation research on service delivery models and community health interventions',
//         'Digital MERL systems for health and nutrition programmes',
//       ],
//       fullPoints: [
//         'Implementation research on service delivery models and community health interventions',
//         'Impact evaluations and learning reviews',
//         'Digital MERL systems for health and nutrition programmes',
//         'Predictive analytics for service utilisation, risk, and resource targeting',
//       ],
//     },
//     {
//       id: 'governance',
//       title: 'Governance, Democracy & Institutional Accountability',
//       icon: <Shield />,
//       cardPoints: [
//         'Political economy analysis and institutional diagnostics',
//         'Governance monitoring frameworks and digital scorecards',
//       ],
//       fullPoints: [
//         'Political economy analysis and institutional diagnostics',
//         'Implementation research on citizen engagement and service accountability',
//         'Governance monitoring frameworks and digital scorecards',
//         'Data systems strengthening for public sector performance',
//       ],
//     },
//     {
//       id: 'climate',
//       title: 'Climate Change, Resilience & Environmental Justice',
//       icon: <Globe />,
//       cardPoints: [
//         'Implementation research on climate adaptation and green livelihoods',
//         'MERL frameworks for climate resilience and environmental programmes',
//       ],
//       fullPoints: [
//         'Implementation research on climate adaptation and green livelihoods',
//         'MERL frameworks for climate resilience and environmental programmes',
//         'Data analytics for vulnerability mapping and resilience tracking',
//         'Systems strengthening for climate governance and planning',
//       ],
//     },
//     {
//       id: 'gender',
//       title: 'Gender Equality, Disability & Social Inclusion',
//       icon: <Users />,
//       cardPoints: [
//         'Social norms and gender-transformative implementation research',
//         'Equity-focused evaluations and safeguarding systems',
//       ],
//       fullPoints: [
//         'Social norms and gender-transformative implementation research',
//         'Equity-focused evaluations and safeguarding systems',
//         'Inclusion-sensitive MERL and disaggregated data systems',
//         'Institutional systems for safeguarding, inclusion, and accountability',
//       ],
//     },
//     {
//       id: 'youth',
//       title: 'Youth, Employment & Future Skills',
//       icon: <TrendingUp />,
//       cardPoints: [
//         'Implementation research on youth employability and entrepreneurship models',
//         'MERL systems for youth and workforce programmes',
//       ],
//       fullPoints: [
//         'Implementation research on youth employability and entrepreneurship models',
//         'MERL systems for youth and workforce programmes',
//         'Data analytics for labour market forecasting and skills alignment',
//         'Systems strengthening for youth services and innovation ecosystems',
//       ],
//     },
//     {
//       id: 'social',
//       title: 'Social Protection & Economic Inclusion',
//       icon: <Activity />,
//       cardPoints: [
//         'Implementation research on cash transfers, livelihoods, and graduation models',
//         'Digital MERL and beneficiary tracking systems',
//       ],
//       fullPoints: [
//         'Implementation research on cash transfers, livelihoods, and graduation models',
//         'Digital MERL and beneficiary tracking systems',
//         'Analytics for targeting, risk scoring, and programme optimisation',
//         'Policy research and systems reform advisory',
//       ],
//     },
//     {
//       id: 'data',
//       title: 'Data Science, Digital Transformation & ICT4D',
//       icon: <Database />,
//       cardPoints: [
//         'Advanced analytics and machine learning for development decision-making',
//         'Digital system design and data integration',
//       ],
//       fullPoints: [
//         'Advanced analytics and machine learning for development decision-making',
//         'Digital system design and data integration',
//         'Implementation research on digital platforms and service delivery models',
//         'Institutional digital transformation strategies',
//       ],
//     },
//     {
//       id: 'community',
//       title: 'Community-Led Development & Participatory Systems',
//       icon: <BookOpen />,
//       cardPoints: [
//         'Participatory implementation research and co-creation',
//         'Community-based MERL and feedback systems',
//       ],
//       fullPoints: [
//         'Participatory implementation research and co-creation',
//         'Community-based MERL and feedback systems',
//         'Local data systems and social accountability tools',
//         'Knowledge translation for community learning and advocacy',
//       ],
//     },
//   ];

//   return (
//     <>
//       {/* Hero */}
//       <section className="hero hero-sectors">
//         <div className="container text-center">
//           <h1 className="text-primary">Our Thematic Focus Areas</h1>
//           <p className="lead">
//             Deep expertise across interconnected thematic domains, supported by applied research,
//             MERL systems, data analytics, and institutional strengthening.
//           </p>
//         </div>
//       </section>

//       {/* Cards */}
//       <section className="section bg-white">
//         <div className="container">
//           <div className="sectors-grid-wrapper">
//             {thematicAreas.map((area) => (
//               <div key={area.id} className="sector-card">
//                 <div className="sector-icon">{area.icon}</div>
//                 <h3>{area.title}</h3>

//                 <ul className="sector-points">
//                   {area.cardPoints.map((point, i) => (
//                     <li key={i}>{point}</li>
//                   ))}
//                 </ul>

//                 <button
//                   className="sector-link"
//                   onClick={() => setActiveSector(area)}
//                 >
//                   Learn More <ArrowRight size={18} />
//                 </button>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Cross-Domain Section */}
//       <section className="section bg-light">
//         <div className="container">
//           <h2 className="text-primary text-center mb-5">Cross-Domain Approach</h2>
//           <p className="lead text-center">
//             We connect thematic areas through integrated, systems-based approaches to address
//             complex and interdependent development challenges.
//           </p>
//         </div>
//       </section>

//       {/* Final CTA */}
//       <section className="final-cta final-cta-sectors text-center">
//         <div className="container">
//           <h2>
//             Ready to Advance Your Development Goals?
//             <br />
//             Let’s Design Evidence-Based Solutions Together.
//           </h2>
//           <a href="/contact" className="btn btn-primary">
//             Get in Touch <ArrowRight size={28} />
//           </a>
//         </div>
//       </section>

//       {/* Footer */}
//       <footer className="footer">
//         <div className="footer-grid container">
//           <div>
//             <div className="footer-logo">DevLearn Impact Research Ltd</div>
//             <p>Plot No. 3788/A, Avondale Road, Lusaka</p>
//             <p>+260 973 902 981 / +260 978 341 825</p>
//             <p>info@devlearnimpact.com</p>
//           </div>
//         </div>
//       </footer>

//       {/* Modal */}
//       {activeSector && (
//         <div className="modal-overlay" onClick={() => setActiveSector(null)}>
//           <div className="modal-content" onClick={(e) => e.stopPropagation()}>
//             <button className="modal-close" onClick={() => setActiveSector(null)}>
//               <X />
//             </button>

//             <div className="modal-header">
//               <div className="modal-icon">{activeSector.icon}</div>
//               <h2>{activeSector.title}</h2>
//             </div>

//             <ul className="modal-list">
//               {activeSector.fullPoints.map((point, i) => (
//                 <li key={i}>{point}</li>
//               ))}
//             </ul>
//           </div>
//         </div>
//       )}
//     </>
//   );
// };

// export default Sectors;


// // pages/Sectors/Sectors.jsx
// import { ArrowRight, BookOpen, Heart, TrendingUp, Users, Shield, GraduationCap, Globe, Zap, Database, Activity } from 'lucide-react';
// import '../../index.css'; // Shared global styles with palette
// import './Sectors.css'; // Page-specific styles

// const Sectors = () => {
// const thematicAreas = [
//   {
//     id: 'education',
//     title: 'Education, Skills, and Learning Systems',
//     icon: <GraduationCap />,
//     description:
//       'Strengthening education systems through evidence-driven reform, assessment, and innovation. This includes implementation research on curriculum reform, teacher professional development, and digital learning; MERL systems for education programmes and learning outcomes; data analytics for assessment systems, learning measurement, and performance monitoring; and policy research and education system diagnostics.',
//   },
//   {
//     id: 'health',
//     title: 'Public Health, Nutrition & Health Systems Strengthening',
//     icon: <Heart />,
//     description:
//       'Supporting resilient, equitable, and data-driven health systems through implementation research on service delivery models and community health interventions, impact evaluations and learning reviews, digital MERL systems for health and nutrition programmes, and predictive analytics for service utilisation, risk profiling, and resource targeting.',
//   },
//   {
//     id: 'governance',
//     title: 'Governance, Democracy & Institutional Accountability',
//     icon: <Shield />,
//     description:
//       'Promoting inclusive, transparent, and accountable governance systems through political economy analysis and institutional diagnostics, implementation research on citizen engagement and service accountability, governance monitoring frameworks and digital scorecards, and data systems strengthening for public sector performance.',
//   },
//   {
//     id: 'climate',
//     title: 'Climate Change, Resilience & Environmental Justice',
//     icon: <Globe />,
//     description:
//       'Supporting climate-resilient, community-led, and sustainable development pathways through implementation research on climate adaptation and green livelihoods, MERL frameworks for climate resilience and environmental programmes, data analytics for vulnerability mapping and resilience tracking, and systems strengthening for climate governance and planning.',
//   },
//   {
//     id: 'gender',
//     title: 'Gender Equality, Disability & Social Inclusion',
//     icon: <Users />,
//     description:
//       'Advancing equity and social transformation through social norms and gender-transformative implementation research, equity-focused evaluations and safeguarding systems, inclusion-sensitive MERL and disaggregated data systems, and institutional systems for safeguarding, inclusion, and accountability.',
//   },
//   {
//     id: 'youth',
//     title: 'Youth, Employment & Future Skills',
//     icon: <TrendingUp />,
//     description:
//       'Equipping young people with the skills and resources needed for productive livelihoods and civic participation through implementation research on youth employability and entrepreneurship models, MERL systems for youth and workforce programmes, data analytics for labour market forecasting and skills alignment, and systems strengthening for youth services and innovation ecosystems.',
//   },
//   {
//     id: 'social',
//     title: 'Social Protection & Economic Inclusion',
//     icon: <Activity />,
//     description:
//       'Strengthening systems that protect vulnerable populations and expand economic opportunity through implementation research on cash transfers, livelihoods, and graduation models; digital MERL and beneficiary tracking systems; analytics for targeting, risk scoring, and programme optimisation; and policy research and systems reform advisory.',
//   },
//   {
//     id: 'data',
//     title: 'Data Science, Digital Transformation & ICT4D',
//     icon: <Database />,
//     description:
//       'Embedding digital innovation across development systems through advanced analytics and machine learning for decision-making, digital system design and data integration, implementation research on digital platforms and service delivery models, and institutional digital transformation strategies.',
//   },
//   {
//     id: 'community',
//     title: 'Community-Led Development & Participatory Systems',
//     icon: <BookOpen />,
//     description:
//       'Placing communities at the centre of sustainable change through participatory implementation research and co-creation, community-based MERL and feedback systems, local data systems and social accountability tools, and knowledge translation for community learning and advocacy.',
//   },
// ];

//   return (
//     <>
//       {/* Hero */}
//       <section className="hero hero-sectors">
//         <div className="container text-center">
//           <h1 className="text-primary">Our Thematic Focus Areas</h1>
//           <p className="lead">
//             Deep expertise across 11 key areas, tailored to Zambia and Africa's development challenges. We deliver context-specific insights that promote equity, resilience, and sustainable progress.
//           </p>
//           <div className="hero-buttons">
//             <a href="#domain-specific" className="btn btn-primary">Domain-Specific</a>
//             <a href="#cross-domain" className="btn btn-secondary">Cross-Domain</a>
//           </div>
//         </div>
//       </section>

//       {/* Domain-Specific Section */}
//       <section id="domain-specific" className="section thematic-section bg-white">
//         <div className="container">
//           <h2 className="text-primary text-center mb-5">Domain-Specific Insights</h2>
//           <p className="lead text-center mb-5">
//             We deliver deep, specialized expertise in individual thematic areas, providing targeted research, evaluation, 
//             and advisory services tailored to specific development challenges in education, health, governance, climate, 
//             and more.
//           </p>

//           <div className="sectors-grid-wrapper">
//             {thematicAreas.map((area) => (
//               <div key={area.id} className="sector-card">
//                 <div className="sector-icon icon-primary">{area.icon}</div>
//                 <h3 className="text-primary">{area.title}</h3>
//                 <p>{area.description}</p>
//                 <a href={`#${area.id}`} className="sector-link">
//                   Learn More <ArrowRight size={20} />
//                 </a>
//               </div>
//             ))}
//           </div>

//           <div className="text-center mt-5">
//             <a href="#cross-domain" className="btn btn-outline">
//               Explore Cross-Domain Approach <ArrowRight />
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Cross-Domain Section */}
//       <section id="cross-domain" className="section thematic-section bg-light">
//         <div className="container">
//           <h2 className="text-primary text-center mb-5">Cross-Domain Approach</h2>
//           <p className="lead text-center mb-5">
//             Our strength lies in connecting multiple thematic areas through integrated, systems-thinking approaches. 
//             We facilitate linkages between domains (e.g. education + health, climate + gender, governance + economic 
//             inclusion) to address complex, interconnected challenges.
//           </p>

//           <div className="approach-grid">
//             <div className="approach-step">
//               <h3 className="text-primary">1. Systems Mapping & Integration</h3>
//               <p>Identify intersections between domains and design holistic interventions.</p>
//             </div>
//             <div className="approach-step">
//               <h3 className="text-primary">2. Multi-Sectoral Evidence Building</h3>
//               <p>Use mixed-methods research to understand cross-cutting impacts and synergies.</p>
//             </div>
//             <div className="approach-step">
//               <h3 className="text-primary">3. Collaborative Implementation</h3>
//               <p>Facilitate partnerships and co-creation across sectors for sustainable outcomes.</p>
//             </div>
//           </div>

//           <div className="text-center mt-5">
//             <a href="#domain-specific" className="btn btn-outline">
//               View Domain-Specific Areas <ArrowRight />
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Final CTA */}
//       <section className="final-cta final-cta-sectors text-center">
//         <div className="container">
//           <h2>Ready to Advance Your Development Goals?<br />Let's Design Evidence-Based Solutions Together.</h2>
//           <a href="/contact" className="btn btn-primary">
//             Get in Touch <ArrowRight size={28} />
//           </a>
//         </div>
//       </section>

//       {/* Footer */}
//       <footer className="footer">
//         <div className="footer-grid container">
//           <div>
//             <div className="footer-logo">DevLearn Impact Research Ltd</div>
//             <p>Plot No. 3788/A, Avondale Road, Lusaka</p>
//             <p>+260 973 902 981 / +260 978 341 825</p>
//             <p>info@devlearnimpact.com</p>
//           </div>
//           <div>
//             <h4>Services</h4>
//             <ul>
//               <li><a href="/services">Applied Research</a></li>
//               <li><a href="/services">MERL Frameworks</a></li>
//               <li><a href="/services">Capacity Building</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Thematic Areas</h4>
//             <ul>
//               <li><a href="/sectors#domain-specific">Domain-Specific</a></li>
//               <li><a href="/sectors#cross-domain">Cross-Domain</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Company</h4>
//             <ul>
//               <li><a href="/about">About</a></li>
//               <li><a href="/team">Team</a></li>
//               <li><a href="/case-studies">Projects</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Connect</h4>
//             <ul>
//               <li><a href="https://www.linkedin.com/company/devlearn-impact-research/">LinkedIn</a></li>
//               <li><a href="https://www.facebook.com/share/1AS3Ahfsy7/">Facebook</a></li>
//             </ul>
//           </div>
//         </div>
//         <div className="copyright">
//           © 2026 DevLearn Impact Research Ltd. All rights reserved.
//         </div>
//       </footer>
//     </>
//   );
// };

// export default Sectors;
// // pages/Sectors/Sectors.jsx
// import { ArrowRight, BookOpen, Heart, TrendingUp, Users, Shield, GraduationCap, Globe, Zap, Database, Activity } from 'lucide-react';
// import '../../index.css';
// import './Sectors.css';

// const Sectors = () => {
//   const thematicAreas = [
//     {
//       id: 'education',
//       title: 'Education and Learning Systems',
//       icon: <GraduationCap />,
//       description: 'Focusing on learning outcomes, curriculum evaluation, assessment systems, and teacher effectiveness to strengthen educational policy and practice.',
//     },
//     {
//       id: 'health',
//       title: 'Public Health and Health Systems Strengthening',
//       icon: <Heart />,
//       description: 'Strengthening health systems through intervention evaluation, disease surveillance, healthcare delivery, and equitable access to promote resilient communities.',
//     },
//     {
//       id: 'governance',
//       title: 'Democracy, Governance, and Civic Engagement',
//       icon: <Shield />,
//       description: 'Promoting inclusive governance, transparency, policy evaluation, and participatory decision-making processes.',
//     },
//     {
//       id: 'climate',
//       title: 'Climate Change, Resilience and Environmental Justice',
//       icon: <Globe />,
//       description: 'Advancing community-based climate adaptation, environmental awareness, green livelihoods, and sustainability planning.',
//     },
//     {
//       id: 'gender',
//       title: 'Gender Equality and Social Norms Transformation',
//       icon: <Users />,
//       description: 'Addressing gender-based disparities through research and programming focused on equity, gender-based violence prevention, and social norms change.',
//     },
//     {
//       id: 'youth',
//       title: 'Youth Development and Skills for the Future',
//       icon: <TrendingUp />,
//       description: 'Enhancing youth employability, entrepreneurship, digital skills, and life skills to prepare young people for the future of work.',
//     },
//     {
//       id: 'social',
//       title: 'Social Protection and Economic Inclusion',
//       icon: <Activity />,
//       description: 'Analysing and strengthening systems for social safety nets, poverty reduction, and inclusive economic policies.',
//     },
//     {
//       id: 'financial',
//       title: 'Financial Inclusion and Livelihood Resilience',
//       icon: <Zap />,
//       description: 'Supporting financial literacy, microfinance, economic empowerment, and household resilience through data-driven insights.',
//     },
//     {
//       id: 'data',
//       title: 'Data Science and Applied Statistics for Development',
//       icon: <Database />,
//       description: 'Applying advanced analytics, statistical modelling, and machine learning to improve program targeting, forecasting, and evaluation.',
//     },
//     {
//       id: 'community',
//       title: 'Community-Led Development and Participatory Approaches',
//       icon: <BookOpen />,
//       description: 'Emphasising bottom-up development models that empower communities through co-creation and participatory planning.',
//     },
//     {
//       id: 'digital',
//       title: 'Digital Innovation and ICT4D',
//       icon: <Zap />,
//       description: 'Leveraging digital tools, platforms, and data collection systems to enhance decision-making, service delivery, and M&E processes.',
//     },
//   ];

//   return (
//     <>
//       {/* Hero */}
//       <section className="hero hero-sectors">
//         <div className="container text-center">
//           <h1 className="text-primary">Our Thematic Focus Areas</h1>
//           <p className="lead">
//             Deep expertise across 11 key areas, tailored to Zambia and Africa's development challenges. We deliver context-specific insights that promote equity, resilience, and sustainable progress.
//           </p>
//           <div className="hero-buttons">
//             <a href="#explore" className="btn btn-primary">Explore Areas <ArrowRight /></a>
//             <a href="/case-studies" className="btn btn-secondary">View Projects</a>
//           </div>
//         </div>
//       </section>

//       {/* Overview Grid */}
//       <section id="explore" className="section sectors-grid">
//         <div className="container">
//           <h2 className="text-center text-primary mb-5">Our 11 Thematic Focus Areas</h2>
//           <div className="sectors-grid-wrapper">
//             {thematicAreas.map((area) => (
//               <div key={area.id} className="sector-card">
//                 <div className="sector-icon icon-primary">{area.icon}</div>
//                 <h3 className="text-primary">{area.title}</h3>
//                 <p>{area.description}</p>
//                 <a href={`#${area.id}`} className="sector-link">
//                   Learn More <ArrowRight size={20} />
//                 </a>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Detailed Thematic Sections */}
//       <div className="thematic-details">
//         {thematicAreas.map((area) => (
//           <section key={area.id} id={area.id} className="section thematic-detail">
//             <div className="container">
//               <div className="thematic-header">
//                 <div className="thematic-icon large">{area.icon}</div>
//                 <h2 className="text-primary">{area.title}</h2>
//               </div>
              
//               <p className="lead">{area.description}</p>
              
//               <div className="thematic-content">
//                 <h3>Our Focus Areas Include:</h3>
//                 <ul>
//                   <li>Context-specific research and evaluation</li>
//                   <li>Policy analysis and recommendations</li>
//                   <li>Capacity building for local stakeholders</li>
//                   <li>Integration of innovative methodologies</li>
//                   <li>Monitoring, evaluation, and learning frameworks</li>
//                 </ul>

//                 <p className="mt-4">
//                   We apply mixed-methods approaches, participatory research, and data-driven insights to deliver measurable impact in this thematic area.
//                 </p>

//                 {/* You can add more content here later: case studies, images, stats, etc. */}
//               </div>

//               <a href="#explore" className="btn btn-outline mt-4">
//                 Back to Overview <ArrowRight />
//               </a>
//             </div>
//           </section>
//         ))}
//       </div>

//       {/* Cross-Thematic Approach */}
//       <section className="section approach bg-light">
//         <div className="container text-center">
//           <h2 className="text-primary">Our Cross-Thematic Approach</h2>
//           <p className="lead">
//             Guided by facilitation, linkage, and partnership, we connect themes through inclusive, adaptive methodologies like RCTs, mixed-methods, and AI analytics.
//           </p>
//           <div className="approach-grid">
//             <div className="approach-step">
//               <h3 className="text-primary">1. Contextual Deep Dive</h3>
//               <p>Assess sector-specific challenges with stakeholder input for tailored strategies.</p>
//             </div>
//             <div className="approach-step">
//               <h3 className="text-primary">2. Rigorous Evidence Gathering</h3>
//               <p>Blend surveys, experiments, and digital tools for comprehensive, ethical data.</p>
//             </div>
//             <div className="approach-step">
//               <h3 className="text-primary">3. Actionable Synthesis</h3>
//               <p>Translate insights into policy, programs, and capacity-building for real change.</p>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* CTA */}
//       <section className="final-cta final-cta-sectors text-center">
//         <div className="container">
//           <h2>Ready to Advance Your Thematic Goals?<br />Let's Co-Create Evidence-Led Solutions.</h2>
//           <a href="/contact" className="btn btn-primary">Discuss Your Focus Area <ArrowRight size={28} /></a>
//         </div>
//       </section>

//       {/* Footer */}
//       <footer className="footer">
//         <div className="footer-grid">
//           {/* ... your existing footer content ... */}
//         </div>
//         <div className="copyright">© 2026 DevLearn Impact Research Ltd. All rights reserved.</div>
//       </footer>
//     </>
//   );
// };

// export default Sectors;

// // pages/Sectors/Sectors.jsx - Thematic Areas page fully branded for DevLearn
// import { ArrowRight, BookOpen, Heart, TrendingUp, Users, Shield, GraduationCap, Globe, Zap, Database, Activity } from 'lucide-react';
// import '../../index.css'; // Shared global styles with palette
// import './Sectors.css'; // Page-specific styles

// const Sectors = () => {
//   const thematicAreas = [
//     {
//       title: 'Education and Learning Systems',
//       icon: <GraduationCap />,
//       description: 'Focusing on learning outcomes, curriculum evaluation, assessment systems, and teacher effectiveness to strengthen educational policy and practice.',
//       link: '#education'
//     },
//     {
//       title: 'Public Health and Health Systems Strengthening',
//       icon: <Heart />,
//       description: 'Strengthening health systems through intervention evaluation, disease surveillance, healthcare delivery, and equitable access to promote resilient communities.',
//       link: '#health'
//     },
//     {
//       title: 'Democracy, Governance, and Civic Engagement',
//       icon: <Shield />,
//       description: 'Promoting inclusive governance, transparency, policy evaluation, and participatory decision-making processes.',
//       link: '#governance'
//     },
//     {
//       title: 'Climate Change, Resilience and Environmental Justice',
//       icon: <Globe />,
//       description: 'Advancing community-based climate adaptation, environmental awareness, green livelihoods, and sustainability planning.',
//       link: '#climate'
//     },
//     {
//       title: 'Gender Equality and Social Norms Transformation',
//       icon: <Users />,
//       description: 'Addressing gender-based disparities through research and programming focused on equity, gender-based violence prevention, and social norms change.',
//       link: '#gender'
//     },
//     {
//       title: 'Youth Development and Skills for the Future',
//       icon: <TrendingUp />,
//       description: 'Enhancing youth employability, entrepreneurship, digital skills, and life skills to prepare young people for the future of work.',
//       link: '#youth'
//     },
//     {
//       title: 'Social Protection and Economic Inclusion',
//       icon: <Activity />,
//       description: 'Analysing and strengthening systems for social safety nets, poverty reduction, and inclusive economic policies.',
//       link: '#social'
//     },
//     {
//       title: 'Financial Inclusion and Livelihood Resilience',
//       icon: <Zap />,
//       description: 'Supporting financial literacy, microfinance, economic empowerment, and household resilience through data-driven insights.',
//       link: '#financial'
//     },
//     {
//       title: 'Data Science and Applied Statistics for Development',
//       icon: <Database />,
//       description: 'Applying advanced analytics, statistical modelling, and machine learning to improve program targeting, forecasting, and evaluation.',
//       link: '#data'
//     },
//     {
//       title: 'Community-Led Development and Participatory Approaches',
//       icon: <BookOpen />,
//       description: 'Emphasising bottom-up development models that empower communities through co-creation and participatory planning.',
//       link: '#community'
//     },
//     {
//       title: 'Digital Innovation and ICT4D',
//       icon: <Zap />,
//       description: 'Leveraging digital tools, platforms, and data collection systems to enhance decision-making, service delivery, and M&E processes.',
//       link: '#digital'
//     }
//   ];

//   if (!thematicAreas || thematicAreas.length === 0) {
//     return <div>Loading thematic areas...</div>;
//   }

//   return (
//     <>
//       {/* Hero */}
//       <section className="hero hero-sectors">
//         <div className="container text-center">
//           <h1 className="text-primary">Our Thematic Focus Areas</h1>
//           <p className="lead">
//             Deep expertise across 11 key areas, tailored to Zambia and Africa's development challenges. We deliver context-specific insights that promote equity, resilience, and sustainable progress.
//           </p>
//           <div className="hero-buttons">
//             <a href="#explore" className="btn btn-primary">Explore Areas <ArrowRight /></a>
//             <a href="/case-studies" className="btn btn-secondary">View Projects</a>
//           </div>
//         </div>
//       </section>

//       {/* Introduction */}
//       <section className="section intro">
//         <div className="container text-center">
//           <h2 className="text-primary">Domain-Specific Insights for Transformative Impact</h2>
//           <p className="lead">
//             Our thematic focus integrates rigorous research with participatory methods to address interconnected challenges in health, education, governance, and beyond—anchored in local ownership and evidence-based practice.
//           </p>
//         </div>
//       </section>

//       {/* Thematic Areas Grid */}
//       <section id="explore" className="section sectors-grid">
//         <div className="container">
//           <div className="sectors-grid-wrapper">
//             {thematicAreas.map((area, index) => (
//               <div key={index} className="sector-card">
//                 <div className="sector-icon icon-primary">{area.icon}</div>
//                 <h3 className="text-primary">{area.title}</h3>
//                 <p>{area.description}</p>
//                 <a href={area.link} className="sector-link">Learn More <ArrowRight size={20} /></a>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Approach */}
//       <section className="section approach bg-light">
//         <div className="container text-center">
//           <h2 className="text-primary">Our Cross-Thematic Approach</h2>
//           <p className="lead">
//             Guided by facilitation, linkage, and partnership, we connect themes through inclusive, adaptive methodologies like RCTs, mixed-methods, and AI analytics.
//           </p>
//           <div className="approach-grid">
//             <div className="approach-step">
//               <h3 className="text-primary">1. Contextual Deep Dive</h3>
//               <p>Assess sector-specific challenges with stakeholder input for tailored strategies.</p>
//             </div>
//             <div className="approach-step">
//               <h3 className="text-primary">2. Rigorous Evidence Gathering</h3>
//               <p>Blend surveys, experiments, and digital tools for comprehensive, ethical data.</p>
//             </div>
//             <div className="approach-step">
//               <h3 className="text-primary">3. Actionable Synthesis</h3>
//               <p>Translate insights into policy, programs, and capacity-building for real change.</p>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* CTA */}
//       <section className="final-cta final-cta-sectors text-center">
//         <div className="container">
//           <h2>Ready to Advance Your Thematic Goals?<br />Let's Co-Create Evidence-Led Solutions.</h2>
//           <a href="/contact" className="btn btn-primary">Discuss Your Focus Area <ArrowRight size={28} /></a>
//         </div>
//       </section>

//       {/* Footer */}
//       <footer className="footer">
//         <div className="footer-grid">
//           <div>
//             <div className="footer-logo">DevLearn Impact Research Ltd</div>
//             <p>Plot No. 3788/A, Avondale Road, Lusaka</p>
//             <p>+260 973 902 981 / +260 978 341 825</p>
//             <p>info@devlearnimpact.com</p>
//           </div>
//           <div>
//             <h4>Services</h4>
//             <ul>
//               <li><a href="/services">Applied Research</a></li>
//               <li><a href="/services">MERL Frameworks</a></li>
//               <li><a href="/services">Capacity Building</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Thematic Areas</h4>
//             <ul>
//               <li><a href="/sectors">Education & Health</a></li>
//               <li><a href="/sectors">Governance & Climate</a></li>
//               <li><a href="/sectors">Economic Inclusion</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Company</h4>
//             <ul>
//               <li><a href="/about">About</a></li>
//               <li><a href="/team">Team</a></li>
//               <li><a href="/case-studies">Projects</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Connect</h4>
//             <ul>
//               <li><a href="https://www.linkedin.com/company/devlearn-impact-research/">LinkedIn</a></li>
//               <li><a href="https://www.facebook.com/share/1AS3Ahfsy7/">Facebook</a></li>
//             </ul>
//           </div>
//         </div>
//         <div className="copyright">© 2025 DevLearn Impact Research Ltd. All rights reserved. Registered in Zambia, June 28, 2025.</div>
//       </footer>
//     </>
//   );
// };

// export default Sectors;

// // pages/Sectors/Sectors.jsx - Thematic Areas page (corrected for content errors and unreachable code review)
// import { ArrowRight, BookOpen, Heart, TrendingUp, Users, Shield, GraduationCap, Globe, Zap, Database, Activity } from 'lucide-react';
// import '../../index.css'; // Shared global styles with palette
// import './Sectors.css'; // Page-specific styles

// const Sectors = () => {
//   const thematicAreas = [
//     {
//       title: 'Education and Learning Systems',
//       icon: <GraduationCap size={56} />,
//       description: 'Focusing on learning outcomes, curriculum evaluation, assessment systems, and teacher effectiveness to strengthen educational policy and practice.',
//       link: '#education'
//     },
//     {
//       title: 'Public Health and Health Systems Strengthening',
//       icon: <Heart size={56} />,
//       description: 'Strengthening health systems through intervention evaluation, disease surveillance, healthcare delivery, and equitable access to promote resilient communities.', // Corrected from PDF copy-paste error
//       link: '#health'
//     },
//     {
//       title: 'Democracy, Governance, and Civic Engagement',
//       icon: <Shield size={56} />,
//       description: 'Promoting inclusive governance, transparency, policy evaluation, and participatory decision-making processes.',
//       link: '#governance'
//     },
//     {
//       title: 'Climate Change, Resilience and Environmental Justice',
//       icon: <Globe size={56} />,
//       description: 'Advancing community-based climate adaptation, environmental awareness, green livelihoods, and sustainability planning.',
//       link: '#climate'
//     },
//     {
//       title: 'Gender Equality and Social Norms Transformation',
//       icon: <Users size={56} />,
//       description: 'Addressing gender-based disparities through research and programming focused on equity, gender-based violence prevention, and social norms change.',
//       link: '#gender'
//     },
//     {
//       title: 'Youth Development and Skills for the Future',
//       icon: <TrendingUp size={56} />,
//       description: 'Enhancing youth employability, entrepreneurship, digital skills, and life skills to prepare young people for the future of work.',
//       link: '#youth'
//     },
//     {
//       title: 'Social Protection and Economic Inclusion',
//       icon: <Activity size={56} />,
//       description: 'Analysing and strengthening systems for social safety nets, poverty reduction, and inclusive economic policies.',
//       link: '#social'
//     },
//     {
//       title: 'Financial Inclusion and Livelihood Resilience',
//       icon: <Zap size={56} />,
//       description: 'Supporting financial literacy, microfinance, economic empowerment, and household resilience through data-driven insights.',
//       link: '#financial'
//     },
//     {
//       title: 'Data Science and Applied Statistics for Development',
//       icon: <Database size={56} />,
//       description: 'Applying advanced analytics, statistical modelling, and machine learning to improve program targeting, forecasting, and evaluation.',
//       link: '#data'
//     },
//     {
//       title: 'Community-Led Development and Participatory Approaches',
//       icon: <BookOpen size={56} />,
//       description: 'Emphasising bottom-up development models that empower communities through co-creation and participatory planning.',
//       link: '#community'
//     },
//     {
//       title: 'Digital Innovation and ICT4D',
//       icon: <Zap size={56} />,
//       description: 'Leveraging digital tools, platforms, and data collection systems to enhance decision-making, service delivery, and M&E processes.',
//       link: '#digital'
//     }
//   ];

//   // Early return if no areas (defensive, prevents unreachable code warnings)
//   if (!thematicAreas || thematicAreas.length === 0) {
//     return <div>Loading thematic areas...</div>;
//   }

//   return (
//     <>
//       {/* Hero */}
//       <section className="hero" style={{ background: 'linear-gradient(180deg, #F0F9FF 0%, #ffffff 100%)' }}>
//         <div className="container">
//           <h1 style={{ color: '#1E3A8A' }}>Our Thematic Focus Areas</h1>
//           <p>
//             Deep expertise across 11 key areas, tailored to Zambia and Africa's development challenges. We deliver context-specific insights that promote equity, resilience, and sustainable progress.
//           </p>
//           <div className="hero-buttons">
//             <a href="#explore" className="btn btn-primary">
//               Explore Areas <ArrowRight />
//             </a>
//             <a href="/case-studies" className="btn btn-secondary">
//               View Projects
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Introduction */}
//       <section className="section intro">
//         <div className="container">
//           <div className="text-center">
//             <h2 style={{ color: '#1E3A8A' }}>Domain-Specific Insights for Transformative Impact</h2>
//             <p className="lead">
//               Our thematic focus integrates rigorous research with participatory methods to address interconnected challenges in health, education, governance, and beyond—anchored in local ownership and evidence-based practice.
//             </p>
//           </div>
//         </div>
//       </section>

//       {/* Thematic Areas Grid */}
//       <section id="explore" className="section sectors-grid">
//         <div className="container">
//           <div className="sectors-grid-wrapper">
//             {thematicAreas.map((area, index) => (
//               <div key={index} className="sector-card">
//                 <div className="sector-icon">
//                   {area.icon}
//                 </div>
//                 <h3 style={{ color: '#1E3A8A' }}>{area.title}</h3>
//                 <p>{area.description}</p>
//                 <a href={area.link} className="sector-link">Learn More <ArrowRight size={20} /></a>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Approach Tie-In */}
//       <section className="section approach bg-light" style={{ background: '#F0F9FF' }}>
//         <div className="container">
//           <div className="text-center">
//             <h2 style={{ color: '#1E3A8A' }}>Our Cross-Thematic Approach</h2>
//             <p className="lead">
//               Guided by facilitation, linkage, and partnership, we connect themes through inclusive, adaptive methodologies like RCTs, mixed-methods, and AI analytics.
//             </p>
//           </div>
//           <div className="approach-grid">
//             <div className="approach-step">
//               <h3 style={{ color: '#1E3A8A' }}>1. Contextual Deep Dive</h3>
//               <p>Assess sector-specific challenges with stakeholder input for tailored strategies.</p>
//             </div>
//             <div className="approach-step">
//               <h3 style={{ color: '#1E3A8A' }}>2. Rigorous Evidence Gathering</h3>
//               <p>Blend surveys, experiments, and digital tools for comprehensive, ethical data.</p>
//             </div>
//             <div className="approach-step">
//               <h3 style={{ color: '#1E3A8A' }}>3. Actionable Synthesis</h3>
//               <p>Translate insights into policy, programs, and capacity-building for real change.</p>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* CTA */}
//       <section className="final-cta text-center" style={{ background: '#1E3A8A', color: '#fff' }}>
//         <div className="container">
//           <h2>
//             Ready to Advance Your Thematic Goals?<br />Let's Co-Create Evidence-Led Solutions.
//           </h2>
//           <a href="/contact" className="btn btn-primary" style={{ background: '#fff', color: '#1E3A8A' }}>
//             Discuss Your Focus Area <ArrowRight size={28} />
//           </a>
//         </div>
//       </section>

//       {/* Footer - Reused with DevLearn details */}
//       <footer className="footer" style={{ background: '#111827' }}>
//         <div className="footer-grid">
//           <div>
//             <div className="footer-logo" style={{ color: '#0D9488' }}>DevLearn Impact Research Ltd</div>
//             <p>Plot No. 3788/A, Avondale Road, Lusaka</p>
//             <p>+260 973 902 981 / +260 978 341 825</p>
//             <p>info@devlearnimpact.com</p>
//           </div>
//           <div>
//             <h4 style={{ color: '#fff' }}>Services</h4>
//             <ul>
//               <li><a href="/services">Applied Research</a></li>
//               <li><a href="/services">MERL Frameworks</a></li>
//               <li><a href="/services">Capacity Building</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4 style={{ color: '#fff' }}>Thematic Areas</h4>
//             <ul>
//               <li><a href="/sectors">Education & Health</a></li>
//               <li><a href="/sectors">Governance & Climate</a></li>
//               <li><a href="/sectors">Economic Inclusion</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4 style={{ color: '#fff' }}>Company</h4>
//             <ul>
//               <li><a href="/about">About</a></li>
//               <li><a href="/team">Team</a></li>
//               <li><a href="/case-studies">Projects</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4 style={{ color: '#fff' }}>Connect</h4>
//             <ul>
//               <li><a href="https://www.linkedin.com/company/devlearn-impact-research/">LinkedIn</a></li>
//               <li><a href="https://www.facebook.com/share/1AS3Ahfsy7/">Facebook</a></li>
//             </ul>
//           </div>
//         </div>
//         <div className="copyright" style={{ color: '#9CA3AF' }}>
//           © 2025 DevLearn Impact Research Ltd. All rights reserved. Registered in Zambia, June 28, 2025.
//         </div>
//       </footer>
//     </>
//   );
// };

// export default Sectors;

// pages/Sectors/Sectors.jsx - Thematic Areas (Sectors) page
// import { ArrowRight, BookOpen, Heart, TrendingUp, Users } from 'lucide-react';
// import '../../index.css'; // Shared global styles
// import './Sectors.css'; // Page-specific styles

// const Sectors = () => {
//   return (
//     <>
//       {/* Hero */}
//       <section className="hero">
//         <div className="container">
//           <h1>Our Thematic Areas</h1>
//           <p>
//             We deliver specialized research across key sectors, tailoring insights to the unique challenges
//             and opportunities in education, health, finance, agriculture, and public policy. Evidence that resonates.
//           </p>
//           <div className="hero-buttons">
//             <a href="#explore" className="btn btn-primary">
//               Explore Sectors <ArrowRight />
//             </a>
//             <a href="/case-studies" className="btn btn-secondary">
//               View Case Studies
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Introduction */}
//       <section className="section intro">
//         <div className="container">
//           <div className="text-center">
//             <h2>Sector-Specific Expertise</h2>
//             <p className="lead">
//               With deep domain knowledge and cross-disciplinary teams, we uncover actionable insights that drive
//               policy, innovation, and sustainable impact. From quantitative evaluations to qualitative deep dives.
//             </p>
//           </div>
//         </div>
//       </section>

//       {/* Sectors Grid */}
//       <section id="explore" className="section sectors-grid">
//         <div className="container">
//           <div className="sectors-grid-wrapper">
//             <div className="sector-card">
//               <div className="sector-icon">
//                 <BookOpen size={56} />
//               </div>
//               <h3>Education</h3>
//               <p>
//                 Evaluating learning outcomes, teacher training programs, and access initiatives to build resilient
//                 education systems that empower future generations.
//               </p>
//               <div className="sector-stats">
//                 <span>150+</span>
//                 <span>Studies</span>
//               </div>
//               <a href="#" className="sector-link">Learn More <ArrowRight size={20} /></a>
//             </div>
//             <div className="sector-card">
//               <div className="sector-icon">
//                 <Heart size={56} />
//               </div>
//               <h3>Public Health</h3>
//               <p>
//                 Assessing intervention efficacy, disease surveillance, and healthcare delivery to inform strategies
//                 that save lives and strengthen communities.
//               </p>
//               <div className="sector-stats">
//                 <span>200+</span>
//                 <span>Evaluations</span>
//               </div>
//               <a href="#" className="sector-link">Learn More <ArrowRight size={20} /></a>
//             </div>
//             <div className="sector-card">
//               <div className="sector-icon">
//                 <TrendingUp size={56} />
//               </div>
//               <h3>Finance & Development</h3>
//               <p>
//                 Analyzing financial inclusion, microfinance impacts, and economic policies to foster inclusive growth
//                 and resilient financial ecosystems.
//               </p>
//               <div className="sector-stats">
//                 <span>100+</span>
//                 <span>Projects</span>
//               </div>
//               <a href="#" className="sector-link">Learn More <ArrowRight size={20} /></a>
//             </div>
//             <div className="sector-card">
//               <div className="sector-icon">
//                 <Users size={56} />
//               </div>
//               <h3>Agriculture & Environment</h3>
//               <p>
//                 Measuring climate adaptation, sustainable farming practices, and supply chain efficiencies to support
//                 food security and environmental stewardship.
//               </p>
//               <div className="sector-stats">
//                 <span>120+</span>
//                 <span>Initiatives</span>
//               </div>
//               <a href="#" className="sector-link">Learn More <ArrowRight size={20} /></a>
//             </div>
//             <div className="sector-card">
//               <div className="sector-icon">
//                 <Users size={56} />
//               </div>
//               <h3>Public Policy & Governance</h3>
//               <p>
//                 Informing regulatory frameworks, citizen engagement, and anti-corruption efforts through rigorous
//                 evidence and stakeholder analysis.
//               </p>
//               <div className="sector-stats">
//                 <span>80+</span>
//                 <span>Policies Influenced</span>
//               </div>
//               <a href="#" className="sector-link">Learn More <ArrowRight size={20} /></a>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Approach */}
//       <section className="section approach bg-light">
//         <div className="container">
//           <div className="text-center">
//             <h2>Our Cross-Sector Approach</h2>
//             <p className="lead">
//               We integrate methodologies like RCTs, mixed-methods, and AI analytics to ensure insights are robust,
//               contextual, and ready for implementation.
//             </p>
//           </div>
//           <div className="approach-grid">
//             <div className="approach-step">
//               <h3>1. Contextual Assessment</h3>
//               <p>Deep dive into sector-specific challenges and stakeholder needs.</p>
//             </div>
//             <div className="approach-step">
//               <h3>2. Rigorous Data Collection</h3>
//               <p>Blending surveys, experiments, and secondary data for comprehensive views.</p>
//             </div>
//             <div className="approach-step">
//               <h3>3. Insight Synthesis</h3>
//               <p>Translating complex data into clear, strategic recommendations.</p>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* CTA */}
//       <section className="final-cta text-center">
//         <div className="container">
//           <h2>
//             Ready to tackle challenges in your sector?<br />Let's craft evidence-based solutions.
//           </h2>
//           <a href="/contact" className="btn btn-primary">
//             Discuss Your Sector <ArrowRight size={28} />
//           </a>
//         </div>
//       </section>

//       {/* Footer - Reused for consistency */}
//       <footer className="footer">
//         <div className="footer-grid">
//           <div>
//             <div className="footer-logo">InSight Research</div>
//           </div>
//           <div>
//             <h4>Services</h4>
//             <ul>
//               <li><a href="#">Market Research</a></li>
//               <li><a href="#">Impact Evaluation</a></li>
//               <li><a href="#">Data Analytics</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Sectors</h4>
//             <ul>
//               <li><a href="#">Education</a></li>
//               <li><a href="#">Public Health</a></li>
//               <li><a href="#">Finance & Development</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Company</h4>
//             <ul>
//               <li><a href="/about">About</a></li>
//               <li><a href="#">Team</a></li>
//               <li><a href="#">Reports</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Contact</h4>
//             <ul>
//               <li><a href="/contact">hello@insightresearch.com</a></li>
//               <li><a href="#">Locations</a></li>
//             </ul>
//           </div>
//         </div>
//         <div className="copyright">
//           © 2025 InSight Research. All rights reserved.
//         </div>
//       </footer>
//     </>
//   );
// };

// export default Sectors;